import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// TypeScript interfaces
export interface EnergyDataPoint {
  interval_start_utc: string;
  interval_end_utc: string;
  coal_and_lignite: number;
  nuclear: number;
  hydro: number;
  natural_gas: number;
  wind: number;
  power_storage: number;
  solar: number;
  other: number;
}

export interface MarketInsightsStackedAreaChartProps {
  data: EnergyDataPoint[];
  colors?: { [key: string]: string };
  unit?: string;
  maxValue?: number;
}

// Schema to display name mapping
const schemaToDisplayMap = {
  'nuclear': 'Nuclear',
  'hydro': 'Hydro', 
  'coal_and_lignite': 'Coal And Lignite',
  'natural_gas': 'Natural Gas',
  'wind': 'Wind',
  'power_storage': 'Power Storage',
  'solar': 'Solar',
  'other': 'Other'
};

// Default energy source colors (using display names)
const defaultEnergyColors = {
  "Nuclear": "#4ade80", // Green
  "Hydro": "#06b6d4", // Cyan
  "Coal And Lignite": "#fb923c", // Orange
  "Natural Gas": "#3b82f6", // Blue
  "Wind": "#22c55e", // Green (darker)
  "Power Storage": "#8b5cf6", // Purple
  "Solar": "#fbbf24", // Yellow
  "Other": "#ef4444", // Red
};

// Mock data for ERCOT (can be used as default)
export const mockERCOTData: EnergyDataPoint[] = [
    { interval_start_utc: "2025-06-16T00:00:00Z", interval_end_utc: "2025-06-16T00:15:00Z", nuclear: 5.5, hydro: 0.1, coal_and_lignite: 7.5, natural_gas: 26.0, wind: 12.3, power_storage: 0, solar: 0, other: 0 },
    { interval_start_utc: "2025-06-16T06:00:00Z", interval_end_utc: "2025-06-16T06:15:00Z", nuclear: 5.0, hydro: 0.1, coal_and_lignite: 8.5, natural_gas: 25.0, wind: 12.0, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T06:15:00Z", interval_end_utc: "2025-06-16T06:30:00Z", nuclear: 5.03, hydro: 0.1, coal_and_lignite: 8.43, natural_gas: 24.88, wind: 11.95, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T06:30:00Z", interval_end_utc: "2025-06-16T06:45:00Z", nuclear: 5.05, hydro: 0.1, coal_and_lignite: 8.35, natural_gas: 24.75, wind: 11.9, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T06:45:00Z", interval_end_utc: "2025-06-16T07:00:00Z", nuclear: 5.07, hydro: 0.1, coal_and_lignite: 8.27, natural_gas: 24.62, wind: 11.85, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T07:00:00Z", interval_end_utc: "2025-06-16T07:15:00Z", nuclear: 5.1, hydro: 0.1, coal_and_lignite: 8.2, natural_gas: 24.5, wind: 11.8, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T07:15:00Z", interval_end_utc: "2025-06-16T07:30:00Z", nuclear: 5.07, hydro: 0.1, coal_and_lignite: 8.15, natural_gas: 24.38, wind: 11.73, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T07:30:00Z", interval_end_utc: "2025-06-16T07:45:00Z", nuclear: 5.05, hydro: 0.1, coal_and_lignite: 8.1, natural_gas: 24.25, wind: 11.65, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T07:45:00Z", interval_end_utc: "2025-06-16T08:00:00Z", nuclear: 5.03, hydro: 0.1, coal_and_lignite: 8.05, natural_gas: 24.12, wind: 11.57, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T08:00:00Z", interval_end_utc: "2025-06-16T08:15:00Z", nuclear: 5.0, hydro: 0.1, coal_and_lignite: 8.0, natural_gas: 24.0, wind: 11.5, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T08:15:00Z", interval_end_utc: "2025-06-16T08:30:00Z", nuclear: 4.97, hydro: 0.1, coal_and_lignite: 7.95, natural_gas: 23.88, wind: 11.43, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T08:30:00Z", interval_end_utc: "2025-06-16T08:45:00Z", nuclear: 4.95, hydro: 0.1, coal_and_lignite: 7.9, natural_gas: 23.75, wind: 11.35, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T08:45:00Z", interval_end_utc: "2025-06-16T09:00:00Z", nuclear: 4.93, hydro: 0.1, coal_and_lignite: 7.85, natural_gas: 23.62, wind: 11.27, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T09:00:00Z", interval_end_utc: "2025-06-16T09:15:00Z", nuclear: 4.9, hydro: 0.1, coal_and_lignite: 7.8, natural_gas: 23.5, wind: 11.2, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T10:00:00Z", interval_end_utc: "2025-06-16T10:15:00Z", nuclear: 4.8, hydro: 0.1, coal_and_lignite: 7.5, natural_gas: 23.0, wind: 11.0, power_storage: 0, solar: 0.0, other: 0 },
    { interval_start_utc: "2025-06-16T12:00:00Z", interval_end_utc: "2025-06-16T12:15:00Z", nuclear: 5.0, hydro: 0.1, coal_and_lignite: 7.0, natural_gas: 23.5, wind: 10.0, power_storage: 0, solar: 2.5, other: 0 },
    { interval_start_utc: "2025-06-16T13:00:00Z", interval_end_utc: "2025-06-16T13:15:00Z", nuclear: 5.1, hydro: 0.1, coal_and_lignite: 7.5, natural_gas: 25.0, wind: 9.5, power_storage: 0, solar: 8.0, other: 0 },
    { interval_start_utc: "2025-06-16T14:00:00Z", interval_end_utc: "2025-06-16T14:15:00Z", nuclear: 5.0, hydro: 0.1, coal_and_lignite: 8.0, natural_gas: 26.5, wind: 9.0, power_storage: 0, solar: 15.5, other: 0 },
    { interval_start_utc: "2025-06-16T15:00:00Z", interval_end_utc: "2025-06-16T15:15:00Z", nuclear: 5.2, hydro: 0.1, coal_and_lignite: 8.5, natural_gas: 28.0, wind: 8.5, power_storage: 0, solar: 20.0, other: 0 },
    { interval_start_utc: "2025-06-16T18:00:00Z", interval_end_utc: "2025-06-16T18:15:00Z", nuclear: 5.1, hydro: 0.1, coal_and_lignite: 8.5, natural_gas: 28.5, wind: 7.0, power_storage: 0, solar: 25.5, other: 0 },
    { interval_start_utc: "2025-06-16T19:00:00Z", interval_end_utc: "2025-06-16T19:15:00Z", nuclear: 5.0, hydro: 0.14, coal_and_lignite: 7.4, natural_gas: 27.95, wind: 6.26, power_storage: 0, solar: 26.44, other: 0 },
    { interval_start_utc: "2025-06-16T20:00:00Z", interval_end_utc: "2025-06-16T20:15:00Z", nuclear: 4.9, hydro: 0.1, coal_and_lignite: 7.0, natural_gas: 27.0, wind: 6.0, power_storage: 0, solar: 26.0, other: 0 },
    { interval_start_utc: "2025-06-16T22:00:00Z", interval_end_utc: "2025-06-16T22:15:00Z", nuclear: 4.8, hydro: 0.1, coal_and_lignite: 6.5, natural_gas: 28.0, wind: 5.0, power_storage: 0, solar: 22.0, other: 0 },
];
  

const MarketInsightsStackedAreaChart: React.FC<MarketInsightsStackedAreaChartProps> = ({
  data,
  colors = defaultEnergyColors,
  unit = "GW",
  maxValue,
}) => {
  // Helper function to safely get color for a source
  const getColorForSource = (source: string): string => {
    return (colors as any)[source] || (defaultEnergyColors as any)[source] || "#6b7280";
  };

  // Helper function to extract hour from UTC timestamp for X-axis positioning
  const getHourFromTimestamp = (timestamp: string): number => {
    const date = new Date(timestamp);
    return date.getUTCHours() + date.getUTCMinutes() / 60;
  };

  // Preprocess data: convert schema names to display names and add hour field
  const processedData = React.useMemo(() => {
    return data.map(point => {
      const processed: any = {
        hour: getHourFromTimestamp(point.interval_start_utc),
        interval_start_utc: point.interval_start_utc,
        interval_end_utc: point.interval_end_utc
      };
      
      // Convert schema field names to display names
      Object.keys(schemaToDisplayMap).forEach(schemaKey => {
        const displayName = schemaToDisplayMap[schemaKey as keyof typeof schemaToDisplayMap];
        processed[displayName] = point[schemaKey as keyof EnergyDataPoint];
      });
      
      return processed;
    });
  }, [data]);

  // Get energy sources using display names (exclude timestamp fields and sources with all zero values)
  const energySources = React.useMemo(() => {
    if (data.length === 0) return [];
    
    // Get display names from the mapping
    const displayNames = Object.values(schemaToDisplayMap);
    
    // Filter out energy sources that have all zero values
    return displayNames.filter(displayName => {
      return data.some(dataPoint => {
        // Find the schema key for this display name
        const schemaKey = Object.keys(schemaToDisplayMap).find(key => 
          schemaToDisplayMap[key as keyof typeof schemaToDisplayMap] === displayName
        ) as keyof EnergyDataPoint;
        
        if (schemaKey) {
          const value = dataPoint[schemaKey];
          return typeof value === 'number' && value > 0;
        }
        return false;
      });
    });
  }, [data]);

  // Calculate dynamic Y-axis domain and ticks
  const yAxisConfig = React.useMemo(() => {
    if (processedData.length === 0) {
      return {
        domain: [0, 100],
        ticks: [0, 20, 40, 60, 80, 100],
        interval: 20
      };
    }

    // Find the maximum total value across all data points
    const maxValue = Math.max(...processedData.map(point => {
      return energySources.reduce((sum, source) => {
        return sum + (Number(point[source]) || 0);
      }, 0);
    }));
    
    // Calculate appropriate interval (minimum 5)
    // Start with 5 and increase until we have reasonable number of ticks (4-8)
    let interval = 5;
    while ((maxValue / interval) > 8) {
      if (interval === 5) interval = 10;
      else if (interval === 10) interval = 20;
      else if (interval === 20) interval = 25;
      else if (interval === 25) interval = 50;
      else if (interval === 50) interval = 100;
      else if (interval === 100) interval = 200;
      else if (interval === 200) interval = 250;
      else if (interval === 250) interval = 500;
      else interval *= 2;
    }
    
    // Calculate the ceiling (highest tick that contains the max value)
    const maxTick = Math.ceil(maxValue / interval) * interval;
    
    // Generate ticks from 0 to maxTick
    const ticks = [];
    for (let i = 0; i <= maxTick; i += interval) {
      ticks.push(i);
    }
    
    return {
      domain: [0, maxTick],
      ticks,
      interval
    };
  }, [processedData, energySources]);

  // Helper function to format energy source display names
  const formatEnergySourceName = (key: string): string => {
    return schemaToDisplayMap[key as keyof typeof schemaToDisplayMap] || key;
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, entry: any) => sum + entry.value, 0);
  
      const formatTime = (hour: number): string => {
        const wholeHour = Math.floor(hour);
        const minutes = Math.round((hour - wholeHour) * 60);
        const ampm = wholeHour < 12 ? "AM" : "PM";
        const displayHour =
          wholeHour === 0 ? 12 : wholeHour > 12 ? wholeHour - 12 : wholeHour;
        return `Jun 16, 2025 ${displayHour}:${minutes
          .toString()
          .padStart(2, "0")} ${ampm} CDT`;
      };
  
      return (
        <div className="bg-white dark:bg-[#2D3748] border border-gray-300 dark:border-gray-600 rounded p-1.5 shadow-lg opacity-80 text-xs max-w-xs">
          <p className="font-semibold text-neutral-600 dark:text-[#CEE0EF] mb-1 text-xs leading-tight">
            {formatTime(label)}
          </p>
          {payload
            .sort((a: any, b: any) => b.value - a.value)
            .map((entry: any, index: number) => {
              const percentage = ((entry.value / total) * 100).toFixed(1);
              return (
                <div
                  key={index}
                  className="flex items-center mb-0.5 last:mb-0"
                >
                  {/* COLOR DOT */}
                  <span
                    style={{
                      backgroundColor: entry.color,
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: "4px",
                      flexShrink: 0,
                    }}
                  ></span>
  
                  {/* TEXT */}
                  <span className="text-xs text-neutral-600 dark:text-[#CEE0EF] leading-tight">
                    {formatEnergySourceName(entry.dataKey)}:{" "}
                    <span className="font-semibold">
                      {entry.value.toFixed(2)} {unit}
                    </span>{" "}
                    ({percentage}%)
                  </span>
                </div>
              );
            })}
        </div>
      );
    }
    return null;
  };
  

  return (
    <div className="w-full h-full bg-white dark:bg-[#2D3748] rounded-lg text-neutral-600 dark:text-[#CEE0EF]">
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart
          data={processedData}
          margin={{
            top: 20,
            right: 20,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-600" />
          <XAxis 
            dataKey="hour"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "currentColor" }}
            domain={[0, 23.75]}
            type="number"
            scale="linear"
            ticks={[0, 6, 12, 18, 24]}
            tickFormatter={(hour) => {
              // Calculate the date based on the hour
              // Assuming the data starts on June 16, 2025
              const baseDate = new Date('2025-06-16T00:00:00Z');
              const currentDate = new Date(baseDate);
              currentDate.setHours(currentDate.getHours() + hour);
              
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const month = monthNames[currentDate.getMonth()];
              const day = currentDate.getDate();
              
              // Show date for major markers (0, 12, 24) and time for 6-hour intervals
              if (hour === 0 || hour === 24) {
                return `${month} ${day}`;
              } else if (hour === 6) {
                return "6a";
              } else if (hour === 12) {
                return "12p";
              } else if (hour === 18) {
                return "6p";
              }
              return "";
            }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "currentColor" }}
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={(value) => `${value} ${unit}`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            iconType="rect"
            layout="horizontal" 
            align="center" 
            verticalAlign="bottom"
            height={36}
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px'
            }}
            formatter={(value) => (
              <span className="text-neutral-600 dark:text-[#CEE0EF]">{value}</span>
            )}
          />

          
          {/* Dynamically create areas for each energy source */}
          {energySources.map((source, index) => (
            <Area
              key={source}
              type="monotone"
              dataKey={source}
              stackId="1"
              fillOpacity={0.9}
              stroke={getColorForSource(source)}
              fill={getColorForSource(source)}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MarketInsightsStackedAreaChart;